import { useEffect, useState } from 'react';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum, useSubscription } from '@medplum/react';
import {
  CREATE_ONLY_OPTIONS,
  UPDATE_ONLY_OPTIONS,
} from '@/consts/subscription.consts';
import { showNotification } from '@mantine/notifications';

interface UseClinicalExaminations {
  clinicalExaminations: Encounter[];
  loading: boolean;
  error: Error | null;
}

export function useClinicalExaminations(
  patient: Patient | undefined
): UseClinicalExaminations {
  const medplum = useMedplum();
  const [clinicalExaminations, setClinicalExaminations] = useState<Encounter[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!patient?.id) {
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    medplum
      .searchResources('Encounter', {
        subject: `Patient/${patient.id}`,
        _sort: '-date',
      })
      .then((results) => {
        if (!active) {
          return;
        }

        // keep only encounters with a reasonCode
        const filtered = results.filter((e) => !!e.reasonCode?.length);
        setClinicalExaminations(filtered);
      })
      .catch((err) => {
        if (active) {
          setError(err);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [patient?.id, medplum]);

  //----------------------------------------------------------------------------
  // Subscription for encounter updates
  useSubscription(
    patient?.id ? `Encounter?subject=Patient/${patient.id}` : '',
    async (bundle) => {
      const encounter = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'Encounter'
      )?.resource as Encounter | undefined;
      if (!encounter?.id) {
        return;
      }

      try {
        if (encounter.reasonCode?.length) {
          setClinicalExaminations((prev) => {
            const exists = prev.find((e) => e.id === encounter.id);
            if (exists) {
              return prev.map((e) => (e.id === encounter.id ? encounter : e));
            }
            return [encounter, ...prev];
          });
        } else {
          setClinicalExaminations((prev) =>
            prev.filter((e) => e.id !== encounter.id)
          );
        }
      } catch (err) {
        showNotification({
          color: 'red',
          title: 'Failed',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    },
    UPDATE_ONLY_OPTIONS
  );

  //----------------------------------------------------------------------------
  // Subscription for new encounter creation
  useSubscription(
    patient?.id ? `Encounter?subject=Patient/${patient.id}` : '',
    async (bundle) => {
      const encounter = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'Encounter')?.resource as Encounter | undefined;
      if (!encounter?.id) {
        return;
      }

      try {
        // Only add if it has a reason code (making it an examination)
        if (encounter.reasonCode?.length) {
          setClinicalExaminations((prev) => {
            const exists = prev.find((e) => e.id === encounter.id);
            if (!exists) {
              // Add new encounter to the beginning of the list
              return [encounter, ...prev];
            }
            return prev;
          });
        }
      } catch (err) {
        showNotification({
          color: 'red',
          title: 'Failed',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    },
    CREATE_ONLY_OPTIONS
  );

  //------------------------------------------------------------------------------
  return { clinicalExaminations, loading, error };
}
